import React, { useEffect, useMemo, useState } from 'react';
import ProjectScheduleService from '../../services/ProjectScheduleService';
import ProjectService from '../../services/ProjectService';
import utility from '../../utils/utility';
import ReactPaginate from 'react-paginate';
import FilterBar from '../../components/FilterBar';
import { filterList } from '../../utils/filterUtils';

const ProjectSchedule = () => {
    const [schedules, setSchedules] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;
    const [searchText, setSearchText] = useState('');
    const [filterProject, setFilterProject] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [formData, setFormData] = useState({
        id: '',
        projectId: '',
        date: '',
        endDate: '',
        activity: '',
        status: 'active'
    });
    const filteredSchedules = useMemo(() => schedules.filter((schedule) => {
        if (!searchText) return true;
        return filterList([schedule], searchText, ['activity', 'projectName', 'status']).length > 0;
    }), [schedules, searchText]);

    const handleFilterChange = (setter) => (event) => {
        setter(event.target.value);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setSearchText('');
        setFilterProject('');
        setFilterStatus('');
        setCurrentPage(1);
    };

    useEffect(() => {
        fetchDropdownData();
        fetchData();
    }, [currentPage, filterProject, filterStatus]);

    const fetchDropdownData = async () => {
        try {
            const [projectsRes] = await Promise.all([
                ProjectService.getAll(1, 100) // Get all projects for dropdown
            ]);
            setProjects(projectsRes.data || []);
        } catch (err) {
            utility.showError('Failed to load dropdown data: ' + err.message);
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const data = await ProjectScheduleService.getAll(currentPage, itemsPerPage, {
                projectId: filterProject,
                status: filterStatus
            });
            setSchedules(data.data || []);
            setPageCount(data.pagination.totalPages || 0);
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (schedule = null) => {
        if (schedule) {
            setEditMode(true);
            setFormData({
                id: schedule.id,
                projectId: schedule.projectId,
                date: schedule.date ? schedule.date.split('T')[0] : '',
                endDate: schedule.endDate ? schedule.endDate.split('T')[0] : '',
                activity: schedule.activity || '',
                status: schedule.status
            });
        } else {
            setEditMode(false);
            setFormData({
                id: '',
                projectId: '',
                date: '',
                endDate: '',
                activity: '',
                status: 'active'
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({
            id: '',
            projectId: '',
            date: '',
            endDate: '',
            activity: '',
            status: 'active'
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            if (editMode) {
                await ProjectScheduleService.update(formData.id, formData);
                utility.showSuccess('Project schedule updated successfully');
            } else {
                await ProjectScheduleService.create(formData);
                utility.showSuccess('Project schedule created successfully');
            }
            handleCloseModal();
            fetchData();
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handlePageClick = (event) => {
        setCurrentPage(event.selected + 1);
    };

    return (
        <div className="container-fluid py-2">
            <div className="row">
                <div className="col-12 text-end mb-3">
                    <button className="btn bg-gradient-dark mb-0" onClick={() => handleOpenModal()}>
                        <i className="material-symbols-rounded text-sm">add</i>&nbsp;&nbsp;Add New Schedule
                    </button>
                </div>
                <div className="col-12">
                    <div className="card my-4">
                        <div className="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
                            <div className="bg-gradient-dark shadow-dark border-radius-lg pt-4 pb-3">
                                <h6 className="text-white text-capitalize ps-3">Project Schedules</h6>
                            </div>
                        </div>
                        <div className="card-body px-0 pb-2">
                            <div className="px-3 mb-3">
                                <div className="bg-white rounded-3 shadow-sm p-3">
                                    <div className="row gy-2 gx-2 align-items-end">
                                        <div className="col-md-4">
                                            <FilterBar
                                                value={searchText}
                                                onChange={setSearchText}
                                                onClear={() => setSearchText('')}
                                                placeholder="Search project schedules..."
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label text-muted small">Project</label>
                                            <select className="form-select" value={filterProject} onChange={handleFilterChange(setFilterProject)}>
                                                <option value="">All Projects</option>
                                                {projects.map((project) => (
                                                    <option key={project.id} value={project.id}>{project.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label text-muted small">Status</label>
                                            <select className="form-select" value={filterStatus} onChange={handleFilterChange(setFilterStatus)}>
                                                <option value="">Any</option>
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                            </select>
                                        </div>
                                        <div className="col-lg-2 col-md-5">
                                            <label className="form-label text-muted small">&nbsp;</label>
                                            <button type="button" className="mt-1 btn btn-outline-secondary w-100" onClick={resetFilters}>
                                                Clear Filters
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="table-responsive p-0">
                                {loading ? (
                                    <div className="p-4 text-center">Loading...</div>
                                ) : (
                                    <table className="table align-items-center mb-0">
                                        <thead>
                                            <tr>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Project</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Date</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">End Date</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Activity</th>
                                                <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Status</th>
                                                <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredSchedules.length === 0 ? (
                                                <tr><td colSpan="5" className="text-center py-4">No project schedules found.</td></tr>
                                            ) : (
                                                filteredSchedules.map((schedule) => (
                                                    <tr key={schedule.id}>
                                                        <td>
                                                            <div className="d-flex px-2 py-1">
                                                                <div className="d-flex flex-column justify-content-center">
                                                                    <h6 className="mb-0 text-sm">{schedule.projectName || 'Unknown'}</h6>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">{schedule.date ? new Date(schedule.date).toLocaleDateString() : 'N/A'}</p>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">{schedule.endDate ? new Date(schedule.endDate).toLocaleDateString() : 'N/A'}</p>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">{schedule.activity || 'N/A'}</p>
                                                        </td>
                                                        <td className="align-middle text-center text-sm">
                                                            <span
                                                                className={`badge badge-sm bg-gradient-${schedule.status === 'active' ? 'success' : 'secondary'}`}
                                                            >
                                                                {schedule.status}
                                                            </span>
                                                        </td>
                                                        <td className="align-middle text-center">
                                                            <button className="btn btn-link text-info mb-0" onClick={() => handleOpenModal(schedule)}>
                                                                Edit
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan="6" className="text-center">
                                                    <ReactPaginate
                                                        previousLabel="<"
                                                        nextLabel=">"
                                                        breakLabel="..."
                                                        pageCount={pageCount}
                                                        marginPagesDisplayed={2}
                                                        pageRangeDisplayed={5}
                                                        onPageChange={handlePageClick}
                                                        forcePage={currentPage - 1}
                                                        containerClassName="pagination"
                                                        pageClassName="page-item"
                                                        pageLinkClassName="page-link"
                                                        previousClassName="page-item"
                                                        previousLinkClassName="page-link"
                                                        nextClassName="page-item"
                                                        nextLinkClassName="page-link"
                                                        activeClassName="active"
                                                        disabledClassName="disabled"
                                                        breakClassName="page-item"
                                                        breakLinkClassName="page-link"
                                                    />
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Modal */}
                {showModal && (
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                            <div className="modal-content">
                                <div className="modal-header border-0">
                                    <h5 className="modal-title font-weight-normal">
                                        {editMode ? 'Edit Project Schedule' : 'Add Project Schedule'}
                                    </h5>
                                    <button type="button" className="btn-close text-dark" aria-label="Close" onClick={handleCloseModal} />
                                </div>
                                <form onSubmit={handleSubmit}>
                                    <div className="modal-body row">
                                        <div className="col-md-6 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>Project *</label>
                                                <select
                                                    name="projectId"
                                                    className="form-control"
                                                    value={formData.projectId}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="">Select Project</option>
                                                    {projects.map(project => (
                                                        <option key={project.id} value={project.id}>
                                                            {project.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>Date *</label>
                                                <input
                                                    type="date"
                                                    name="date"
                                                    className="form-control"
                                                    value={formData.date}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>End Date</label>
                                                <input
                                                    type="date"
                                                    name="endDate"
                                                    className="form-control"
                                                    value={formData.endDate}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-12 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>Activity</label>
                                                <input
                                                    type="text"
                                                    name="activity"
                                                    className="form-control"
                                                    value={formData.activity}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter activity description"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>Status</label>
                                                <select
                                                    name="status"
                                                    className="form-control"
                                                    value={formData.status}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="active">Active</option>
                                                    <option value="inactive">Inactive</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="modal-footer border-0">
                                        <button type="button" className="btn bg-gradient-secondary" onClick={handleCloseModal} disabled={saving}>Cancel</button>
                                        <button type="submit" className="btn bg-gradient-dark" disabled={saving}>
                                            {saving ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    {editMode ? 'Updating...' : 'Saving...'}
                                                </>
                                            ) : (
                                                editMode ? 'Update' : 'Save'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProjectSchedule;